import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const fields = [
  ['buyer_name', 'Buyer – Firma', 160], ['buyer_street', 'Buyer – Straße', 160], ['buyer_postal_code', 'Buyer – PLZ', 20],
  ['buyer_city', 'Buyer – Ort', 160], ['buyer_country', 'Buyer – Land', 160],
  ['marking_main_en', 'Hauptseiten 2 + 4 (EN)', 400], ['marking_narrow_en', 'Schmalseiten 1 + 3 (EN)', 400],
  ['marking_inner_en', 'Innenkarton (EN)', 400], ['packing_instructions_en', 'Packvorgaben (EN)', 800], ['footer_en', 'Fußtext (EN)', 300],
] as const;
type TextField = typeof fields[number][0];
type Settings = Record<TextField | 'logo_png_base64', string | null>;
interface SettingsResponse { overrides: Settings | null; effective: Settings }
const pathFor = (scope: string) => `/purchase/supplier-pdf-settings/${scope === 'global' ? 'global' : `suppliers/${scope}`}/`;
const errorText = (error: unknown) => error instanceof Error ? error.message : 'PDF-Einstellungen konnten nicht gespeichert werden';

function SettingsEditor({ scope }: { scope: string }) {
  const queryClient = useQueryClient();
  const global = scope === 'global';
  const path = pathFor(scope);
  const query = useQuery({ queryKey: ['purchase', 'supplier-pdf-settings', scope], queryFn: () => api.get<SettingsResponse>(path), refetchOnWindowFocus: false });
  const globalQuery = useQuery({ queryKey: ['purchase', 'supplier-pdf-settings', 'global'], queryFn: () => api.get<SettingsResponse>(pathFor('global')), enabled: !global, refetchOnWindowFocus: false });
  const [draft, setDraft] = useState<Partial<Settings>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const lock = useRef(false);
  useEffect(() => { if (query.data) setDraft(global ? query.data.effective : query.data.overrides ?? {}); }, [query.data, global]);
  if (query.isPending || (!global && globalQuery.isPending)) return <p className="text-sm text-ink-2">PDF-Einstellungen werden geladen…</p>;
  if (query.isError || (!global && globalQuery.isError)) return <p role="alert" className="text-sm text-coral">{errorText(query.error ?? globalQuery.error)}</p>;
  const effective = global ? query.data.effective : globalQuery.data!.effective;
  const update = (key: keyof Settings, value: string | null) => { setSaved(false); setDraft(previous => ({ ...previous, [key]: value })); };
  async function save(reset = false) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(''); setSaved(false);
    try {
      const body = Object.fromEntries([...fields.map(([key]) => [key, draft[key] ?? null]), ['logo_png_base64', draft.logo_png_base64 ?? null]]);
      const response = reset ? await api.delete<SettingsResponse>(path) : await api.put<SettingsResponse>(path, body);
      queryClient.setQueryData(['purchase', 'supplier-pdf-settings', scope], response);
      setDraft(global ? response.effective : response.overrides ?? {});
      setSaved(true);
    } catch (err) { setError(errorText(err)); }
    finally { lock.current = false; setBusy(false); }
  }
  const logo = draft.logo_png_base64 ?? effective.logo_png_base64;
  return <div className="space-y-5">
    <fieldset disabled={busy} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map(([key, label, max]) => {
          const inherited = !global && draft[key] == null;
          const value = draft[key] ?? effective[key] ?? '';
          const id = `pdf-${key}`;
          return <div key={key} className={key.startsWith('buyer_') ? 'space-y-1.5' : 'space-y-1.5 sm:col-span-2'}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label htmlFor={id} className="text-sm font-medium text-ink">{label}</label>
              {!global && <label className="flex items-center gap-2 text-xs text-ink-2">
                <input type="checkbox" aria-label={`${label}: global verwenden`} checked={inherited} onChange={event => update(key, event.target.checked ? null : effective[key] ?? '')} /> Global verwenden
              </label>}
            </div>
            {key.startsWith('buyer_')
              ? <Input id={id} value={value} disabled={inherited} maxLength={max} onChange={event => update(key, event.target.value)} />
              : <Textarea id={id} value={value} disabled={inherited} rows={key === 'footer_en' ? 2 : 3} maxLength={max} onChange={event => update(key, event.target.value)} />}
          </div>;
        })}
      </div>
      <div className="space-y-2 border-t border-line pt-4">
        <label htmlFor="pdf-logo" className="text-sm font-medium">STAHLIA-Logo (PNG, max. 256 KB)</label>
        {!global && <label className="flex items-center gap-2 text-xs text-ink-2">
          <input type="checkbox" aria-label="Logo: global verwenden" checked={draft.logo_png_base64 == null} onChange={event => update('logo_png_base64', event.target.checked ? null : effective.logo_png_base64)} /> Global verwenden
        </label>}
        {logo && <img src={`data:image/png;base64,${logo}`} alt="Aktuelles STAHLIA-Logo" className="max-h-16 max-w-56 object-contain" />}
        <Input id="pdf-logo" type="file" accept="image/png" disabled={!global && draft.logo_png_base64 == null} onChange={async event => {
          const file = event.target.files?.[0]; if (!file) return;
          if (file.size > 256 * 1024 || file.type !== 'image/png') { setError('Bitte eine PNG-Datei mit maximal 256 KB wählen.'); event.target.value = ''; return; }
          try {
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader(); reader.onload = () => resolve(String(reader.result).split(',')[1] ?? ''); reader.onerror = () => reject(new Error('Logo konnte nicht gelesen werden')); reader.readAsDataURL(file);
            });
            update('logo_png_base64', base64); setError('');
          } catch (err) { setError(errorText(err)); }
        }} />
      </div>
    </fieldset>
    {error && <p role="alert" className="text-sm text-coral">{error}</p>}
    {saved && <p role="status" className="text-sm text-ink-2">PDF-Einstellungen gespeichert.</p>}
    <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
      {!global && <Button variant="outline" disabled={busy} onClick={() => void save(true)}>Globale Vorgaben verwenden</Button>}
      <Button disabled={busy} onClick={() => void save()}>{busy ? 'Speichert…' : 'Speichern'}</Button>
    </div>
  </div>;
}

function SettingsDialog() {
  const [scope, setScope] = useState('global');
  const suppliers = useQuery({ queryKey: ['purchase', 'supplier-pdf-suppliers'], queryFn: () => api.get<{ suppliers: Array<{ id: number; name: string }> }>('/purchase/supplier-pdf-settings/suppliers/') });
  return <>
    <label className="block space-y-1.5 text-sm font-medium">Geltungsbereich
      <select aria-label="Geltungsbereich" className="w-full rounded-md border border-line bg-background px-3 py-2 text-sm text-ink" value={scope} onChange={event => setScope(event.target.value)}>
        <option value="global">Global – alle Lieferanten</option>
        {(suppliers.data?.suppliers ?? []).map(s => <option key={s.id} value={String(s.id)}>{s.name || `Lieferant ${s.id}`}</option>)}
      </select>
    </label>
    {suppliers.isError && <p role="alert" className="text-sm text-coral">Lieferanten konnten nicht geladen werden.</p>}
    <p className="text-xs text-ink-2">Platzhalter: {'{field1}, {field2}, {order_number}, {ean}'}. Leerer optionaler Text blendet ihn aus. Das Logo steht zusätzlich auf den Hauptseiten, der Barcode zusätzlich auf dem Innenkarton.</p>
    <SettingsEditor key={scope} scope={scope} />
  </>;
}
export function PdfGrundeinstellungen() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  if (!user?.permissions.article_purchase) return null;
  return <>
    <Button size="sm" variant="outline" onClick={() => setOpen(true)}>PDF-Grundeinstellungen</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>PDF-Grundeinstellungen</DialogTitle>
          <DialogDescription>Englische Lieferanten-PDF: Änderungen gelten für alle späteren Downloads, auch für alte Bestellungen. Lieferanten können einzelne globale Vorgaben überschreiben.</DialogDescription>
        </DialogHeader>
        {open && <SettingsDialog />}
      </DialogContent>
    </Dialog>
  </>;
}
